/** Kept independent of auth, analytics and access stores so an early region
 * lookup can overlap the application download without assigning an offer. */
const RECEIPT_KEY = "twelve-volt:consumer-offer-receipt:v1";
export const OFFER_REGION_PREFETCH_TTL_MS = 240_000;
type RegionProof = { payload: string; signature: string };
type RegionRequest = { token: string; expiresAt: number; promise: Promise<RegionProof | null> };
let pending: RegionRequest | null = null;

export function getOfferReceipt(): string | null {
  try {
    let token = localStorage.getItem(RECEIPT_KEY);
    if (!token || !/^[0-9a-f]{64}$/.test(token)) {
      token = [...crypto.getRandomValues(new Uint8Array(32))]
        .map((byte) => byte.toString(16).padStart(2, "0")).join("");
      localStorage.setItem(RECEIPT_KEY, token);
    }
    return localStorage.getItem(RECEIPT_KEY) === token ? token : null;
  } catch { return null; }
}

/** This only checks whether a prefetched envelope is usable. The offer server
 * still verifies its signature and country before assigning any product. */
function regionExpiration(value: unknown, token: string, serverDate: string | null): number | null {
  if (!value || typeof value !== "object") return null;
  const proof = value as Partial<RegionProof>;
  if (typeof proof.payload !== "string" || proof.payload.length > 512 ||
      typeof proof.signature !== "string" || !/^[0-9a-f]{64}$/.test(proof.signature)) return null;
  try {
    const claims = JSON.parse(proof.payload);
    // Use the edge response clock when supplied; a visitor’s device clock
    // must not invalidate a fresh, correctly signed proof.
    const serverNow = Date.parse(serverDate ?? "");
    const now = Number.isFinite(serverNow) ? serverNow : Date.now();
    if (claims.version !== 1 || claims.token !== token || !Number.isFinite(claims.issuedAt) ||
        claims.issuedAt > now + 30_000 || typeof claims.country !== "string" ||
        !/^[A-Z]{2}$/.test(claims.country) || claims.country === "XX") return null;
    // The server allows five minutes; leave a full minute for resolution and
    // network transit rather than forwarding a proof at the edge of expiry.
    const remaining = claims.issuedAt + OFFER_REGION_PREFETCH_TTL_MS - now;
    return remaining > 0 ? performance.now() + remaining : null;
  } catch { return null; }
}

export function readOfferRegion(token: string): Promise<RegionProof | null> {
  if (!/^[0-9a-f]{64}$/.test(token)) return Promise.resolve(null);
  if (pending?.token === token && performance.now() < pending.expiresAt) return pending.promise;
  const request: RegionRequest = {
    token, expiresAt: performance.now() + OFFER_REGION_PREFETCH_TTL_MS, promise: Promise.resolve(null),
  };
  request.promise = (async () => {
    try {
      const response = await fetch("/api/offer-region", {
        method: "POST", cache: "no-store", credentials: "same-origin",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
        signal: AbortSignal.timeout(5_000),
      });
      if (!response.ok) return null;
      const proof: unknown = await response.json();
      const expiresAt = regionExpiration(proof, token, response.headers.get("Date"));
      if (expiresAt === null) return null;
      request.expiresAt = Math.min(request.expiresAt, expiresAt);
      return proof as RegionProof;
    } catch {
      // A stored offer can still resolve without location. Never mint a local
      // country or product as a fallback for an unreachable region service.
      return null;
    }
  })();
  pending = request;
  void request.promise.then((proof) => { if (!proof && pending === request) pending = null; });
  return request.promise;
}

export function prefetchOfferRegion(): void {
  const token = getOfferReceipt();
  if (token) void readOfferRegion(token);
}
